import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, CircularProgress, IconButton, TextField, Tooltip, Typography } from "@material-ui/core";
import { ArrowDownward, ChatBubbleOutline, Send } from "@material-ui/icons";
import { AuthContext } from "../../context/Auth/AuthContext";
import useStyles from "./styles";

const dateLabel = value => new Date(value).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
const timeLabel = value => new Date(value).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

export default function ChatMessages({ chat, messages, handleSendMessage, handleLoadMore, pageInfo, loading, sending, drafts, setDrafts }) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [pending, setPending] = useState(false);
  const [newMessages, setNewMessages] = useState(false);
  const lock = useRef(false);
  const viewport = useRef(null);
  const anchor = useRef(null);
  const nearBottom = useRef(true);
  const previous = useRef({ chatId: null, first: null, last: null });
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const content = drafts[chat.id] || "";
  const bottom = () => {
    if (viewport.current) viewport.current.scrollTop = viewport.current.scrollHeight;
    nearBottom.current = true; setNewMessages(false);
  };
  useLayoutEffect(() => {
    const container = viewport.current;
    if (!container) return;
    const first = messages[0]?.id;
    const last = messages[messages.length - 1];
    if (previous.current.chatId !== chat.id || !previous.current.last) {
      anchor.current = null; bottom();
    } else if (anchor.current) {
      if (!loading) {
        container.scrollTop = anchor.current.top + container.scrollHeight - anchor.current.height;
        anchor.current = null;
      }
    } else if (last?.id !== previous.current.last) {
      if (nearBottom.current || Number(last?.senderId) === Number(user.id)) bottom();
      else setNewMessages(true);
    }
    previous.current = { chatId: chat.id, first, last: last?.id };
  }, [messages, chat.id, user.id, loading]);

  const older = async () => {
    if (!viewport.current || loading) return;
    anchor.current = { top: viewport.current.scrollTop, height: viewport.current.scrollHeight };
    await handleLoadMore();
  };
  const submit = async () => {
    if (lock.current || sending || !content.trim()) return;
    const targetId = chat.id;
    lock.current = true; setPending(true);
    try {
      const success = await handleSendMessage(content);
      if (success) setDrafts(current => current[targetId] === content ? { ...current, [targetId]: "" } : current);
    } finally { lock.current = false; if (alive.current) setPending(false); }
  };

  return <div className={classes.messageArea}>
    <div ref={viewport} className={classes.messages} role="region" aria-label="Mensagens da conversa" aria-busy={loading}
      onScroll={event => { const node = event.currentTarget; nearBottom.current = node.scrollHeight - node.scrollTop - node.clientHeight < 80; if (nearBottom.current) setNewMessages(false); }}>
      {(pageInfo.hasMore || pageInfo.error) && <div className={classes.older}><Button onClick={older} disabled={loading}>{pageInfo.error ? "Tentar carregar mensagens novamente" : "Mensagens anteriores"}</Button></div>}
      {loading && <div className={classes.older}><CircularProgress size={22} aria-label="Carregando mensagens" /></div>}
      {!messages.length && !loading && !pageInfo.error && <div className={classes.empty}><ChatBubbleOutline /><Typography>Comece a conversa</Typography></div>}
      {messages.map((message, index) => {
        const own = Number(message.senderId) === Number(user.id);
        const day = dateLabel(message.createdAt);
        return <React.Fragment key={message.id}>
          {(index === 0 || dateLabel(messages[index - 1].createdAt) !== day) && <div className={classes.date}><span>{day}</span></div>}
          <article aria-label={`Mensagem de ${own ? "você" : message.sender?.name || "participante"}`} className={`${classes.bubble} ${own ? classes.outgoing : ""}`}>
            {!own && <div className={classes.sender}>{message.sender?.name || "Participante"}</div>}
            <div className={classes.messageText}>{message.message}</div>
            <div className={classes.messageTime}><time dateTime={message.createdAt} title={new Date(message.createdAt).toLocaleString("pt-BR")}>{timeLabel(message.createdAt)}</time></div>
          </article>
        </React.Fragment>;
      })}
    </div>
    {newMessages && <Button variant="contained" color="primary" size="small" startIcon={<ArrowDownward />} className={classes.newMessages} onClick={bottom}>Novas mensagens</Button>}
    <form className={classes.composer} onSubmit={event => { event.preventDefault(); submit(); }}>
      <TextField multiline maxRows={5} variant="outlined" size="small" placeholder="Escreva uma mensagem…" className={classes.input} value={content}
        disabled={pending || sending} inputProps={{ "aria-label": "Mensagem", maxLength: 10000 }}
        onChange={event => { const value = event.target.value; setDrafts(current => ({ ...current, [chat.id]: value })); }}
        onKeyDown={event => { if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); submit(); } }} />
      <Tooltip title="Enviar mensagem"><span><IconButton className={classes.send} type="submit" aria-label="Enviar mensagem" disabled={pending || sending || !content.trim()}>{pending || sending ? <CircularProgress size={20} color="inherit" /> : <Send fontSize="small" />}</IconButton></span></Tooltip>
    </form>
  </div>;
}
